import { ORDER_STATUS_OPTIONS } from './orderStatusOptions';
import baseURL from './base-url';
import { getThumbnailUrl } from '@/helpers/thumbnail';
import type { Order } from '@cutr/constants/order';
import { ORDER_STATUS_ACTIVE_EXCLUDE } from '@cutr/constants/order';
import type { ToBeFixed } from '@cutr/constants/utility-types';
import type { OrderSummary } from '@/types/order';
import type { TimelineMilestone } from '@cutr/constants/timeline';
import dayjs from 'dayjs';

const TEXT_SEARCH_PROPS = [
  'order_id',
  'customer.companyName',
  'customer.name',
  'customer.email',
  'file.name',
  'name',
];

const USER_TYPE_ORDER_STATUS_DELETION_ALLOWED = {
  Customer: ['none', 'draft'],
  Admin: ['none', 'draft', 'submitted', 'quote_request'],
};

const options = ORDER_STATUS_OPTIONS as Record<Order['orderstatus'], number>;

export function isDeletableOrder(
  order: OrderSummary,
  userType: keyof typeof USER_TYPE_ORDER_STATUS_DELETION_ALLOWED
) {
  const deletableStatuses = USER_TYPE_ORDER_STATUS_DELETION_ALLOWED[userType];
  if (!deletableStatuses) return false;
  return deletableStatuses.includes(order.orderstatus);
}

export function orderHasText(order: Order | OrderSummary, searchText: string) {
  const searchString = TEXT_SEARCH_PROPS.map((prop) => {
    const props = prop.split('.');
    let value = order;
    for (const p of props) {
      value = value?.[p as keyof typeof value];
    }
    return value || '';
  })
    .join()
    .toLowerCase();
  return normalizeSearchText(searchString).includes(
    normalizeSearchText(searchText)
  );
}

export function getAcceptedQuote(order: Order | OrderSummary) {
  return order?.customerQuotes?.find((q) => q.status === 'accepted');
}

export function getReadyQuote(order: Order | OrderSummary) {
  return order?.customerQuotes?.find((q) => q.status === 'ready');
}

export function hasReplacedQuote(order: Order | OrderSummary) {
  return Boolean(order?.customerQuotes?.find((q) => q.status === 'replaced'));
}

export function getAcceptedOffer(order: Order | OrderSummary) {
  return order?.makerOffers?.find((q) => q.status === 'accepted');
}

export function orderHasStatus(
  order: Order | OrderSummary,
  status: Order['orderstatus'] | string
) {
  if (!status || status === 'any') return true;
  if (status === 'active')
    return !ORDER_STATUS_ACTIVE_EXCLUDE.includes(
      order.orderstatus as (typeof ORDER_STATUS_ACTIVE_EXCLUDE)[number]
    );
  return order.orderstatus === status;
}

export function orderHasTag(order: Order | OrderSummary, tag: string) {
  if (!tag) return true;
  return order.tags.indexOf(tag) !== -1;
}

export function orderHasOwner(order: Order | OrderSummary, owner: string) {
  if (owner === 'any') return true;
  return order.orderOwners.some((o) => o.userId === owner);
}

export function orderHasMaker(order: Order | OrderSummary, maker = 'any') {
  if (maker === 'any') return true;
  return order.makerId === maker;
}

export function orderHasCustomerOrganisation(
  order: Order | OrderSummary,
  customerOrganisation = 'any'
) {
  if (customerOrganisation === 'any') return true;
  return order.customerOrganisationId === customerOrganisation;
}

export function orderHasTimeline(
  order: Order | OrderSummary,
  timelineFilter: string
) {
  if (
    timelineFilter === 'any' ||
    !('productionTimeline' in order) ||
    !order.productionTimeline?.milestones
  )
    return true;

  if (timelineFilter === 'overdue') {
    return order.productionTimeline.milestones.some(
      (m: TimelineMilestone) =>
        dayjs(m.dueAt).diff(dayjs(), 'day') < 0 && !m.isDone
    );
  }

  if (timelineFilter === 'datesMissing') {
    return order.productionTimeline.milestones.some(
      (m: TimelineMilestone) => !m.dueAt
    );
  }

  return true;
}

// Falls back to the milestone because `deliveryDate` is only set once the order is delivered.
export function getOrderDeliveryDate(
  order: Pick<OrderSummary, 'deliveryDate' | 'productionTimeline'>,
  milestones: TimelineMilestone[] | undefined = order.productionTimeline
    ?.milestones
) {
  if (order.deliveryDate) return order.deliveryDate;
  return milestones?.find((m) => m.type === 'delivery-date')?.dueAt ?? null;
}

function normalizeSearchText(text: string) {
  return text.toLowerCase().replace(/[\W_]/g, '');
}

export function orderStatusGreaterOrEqual(
  order: Order | OrderSummary,
  status: Order['orderstatus']
) {
  if (!order) return false;

  if (options[order.orderstatus] === undefined || options[status] === undefined)
    return false;

  return options[order.orderstatus] >= options[status];
}

export function orderStatusLessThen(
  order: Order | OrderSummary,
  status: Order['orderstatus']
) {
  if (!order) return false;

  if (options[order.orderstatus] === undefined || options[status] === undefined)
    return false;

  return options[order.orderstatus] < options[status];
}

export function orderIsLocked(order: Order) {
  if (getAcceptedQuote(order)) return true;

  if (order.customerInvoices && order.customerInvoices.length > 0) return true;
  return !!order.supplierInvoices && order.supplierInvoices.length > 0;
}

export const makeEmptyOrder = () =>
  ({
    id: '',
    order_id: undefined,
    orderstatus: 'none',
    customer: '',
    maker: undefined,
    name: '',
    additionalInfo: '',
    adminAdditionalInfo: '',
    leftOverMaterialManagement: 'discard',
    delivery: 'Delivery',
    requestedDeliveryDate: undefined,
    customerFulfilmentAddress: null,
    customerFulfilmentAddressId: null,

    customerQuotes: [],
    customerInvoices: [],
    supplierInvoices: [],
    quantity: 1,
    optimizeFor: null,
    targetPriceAmount: 0,
    targetPriceType: null,
    orderVariants: null,
  }) as ToBeFixed as Order;

export function orderCommonAttributes(order: Order | OrderSummary) {
  const { companyName, name, email } = order.customer || {};

  let customerName = name || email;
  if (companyName && customerName !== companyName) {
    customerName += ` (${companyName})`;
  }

  const displayName = order.name || order.shortId || '';

  // not type OrderSummary
  if (!('hasCutlist' in order)) {
    return {
      customerName,
      displayName,
    };
  }

  const hasManufacturingIssues = Boolean(
    (order.cadFileManufacturingIssueCount || 0) > 0
  );
  return {
    isCutlist: order.hasCutlist,
    fileType: order.fileTypes,
    fileNum: order.cadFileCount,
    customerName,
    displayName,
    thumbnailUrl: order.hasCutlist
      ? '/img/cutlist.svg'
      : getThumbnailUrl(order, 'md') ||
        // TODO: CTR-8496 Deprecate thumbnail endpoint once GET /v2/orders/:id
        // resolves order.thumbnails itself, so getThumbnailUrl() above always
        // has something to read and this redirect fallback becomes dead.
        `${baseURL}/v2/orders/${order.id}/thumbnail`,
    hasManufacturingIssues,
  };
}
